import { useState, useEffect } from 'react'
import { templateApi, ProjectFormConfig, ProjectFormField } from '@/services/templateApi'
import { useCurrentTemplate } from './useCurrentTemplate'

interface UseProjectFormReturn {
  projectFormConfig: ProjectFormConfig | null
  loading: boolean
  error: string | null
  currentTemplate: any
  refreshConfig: () => Promise<void>
  // Helper functions
  getFieldConfig: (fieldName: string) => ProjectFormField | undefined
  hasField: (fieldName: string) => boolean
  getFieldOptions: (fieldName: string) => string[]
  getFieldLabel: (fieldName: string) => string
  getFieldPlaceholder: (fieldName: string) => string
  isFieldRequired: (fieldName: string) => boolean
  getFieldsBySection: (section?: string) => ProjectFormField[]
  getSections: () => string[]
}

export const useProjectForm = (): UseProjectFormReturn => {
  const [projectFormConfig, setProjectFormConfig] = useState<ProjectFormConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Use the existing useCurrentTemplate hook to avoid duplicate API calls
  const { template: currentTemplate, loading: templateLoading, error: templateError } = useCurrentTemplate()

  const loadProjectFormConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      
      console.log('🔧 Loading project form config for template:', currentTemplate?.businessType)
      
      // Only load project form config - template is handled by useCurrentTemplate
      const config = await templateApi.getUserProjectFormConfig()
      
      console.log('✅ Project form config loaded:', {
        fieldsCount: config?.projectFields?.length || 0,
        fields: config?.projectFields?.map(f => f.name) || []
      })
      
      setProjectFormConfig(config)
    } catch (err) {
      console.error('❌ Error loading project form config:', err)
      setError('Failed to load project form configuration')
    } finally {
      setLoading(false)
    }
  }

  const refreshConfig = async () => {
    await loadProjectFormConfig()
  }

  useEffect(() => {
    console.log('🔄 useProjectForm effect triggered:', {
      hasTemplate: !!currentTemplate,
      templateLoading,
      templateError,
      templateBusinessType: currentTemplate?.businessType
    })
    
    // Only load project form config if we have a template
    if (currentTemplate && !templateLoading && !templateError) {
      console.log('✅ Conditions met, loading project form config')
      loadProjectFormConfig()
    } else {
      console.log('⏳ Waiting for template or template is loading/error')
    }
  }, [currentTemplate, templateLoading, templateError])

  // Helper function to get field configuration
  const getFieldConfig = (fieldName: string): ProjectFormField | undefined => {
    return projectFormConfig?.projectFields.find(field => field.name === fieldName)
  }

  // Helper function to check if field exists
  const hasField = (fieldName: string): boolean => {
    return !!getFieldConfig(fieldName)
  }

  // Helper function to get field options
  const getFieldOptions = (fieldName: string): string[] => {
    const field = getFieldConfig(fieldName)
    return field?.options || []
  }

  // Helper function to get field label
  const getFieldLabel = (fieldName: string): string => {
    const field = getFieldConfig(fieldName)
    return field?.label || fieldName
  }

  // Helper function to get field placeholder
  const getFieldPlaceholder = (fieldName: string): string => {
    const field = getFieldConfig(fieldName)
    return field?.placeholder || ''
  }

  // Helper function to check if field is required
  const isFieldRequired = (fieldName: string): boolean => {
    const field = getFieldConfig(fieldName)
    return field?.required || false
  }

  // Helper function to get fields by section
  const getFieldsBySection = (section?: string): ProjectFormField[] => {
    if (!projectFormConfig) return []
    
    return projectFormConfig.projectFields.filter(field => {
      if (!section) return !field.section
      return field.section === section
    })
  }

  // Helper function to get all sections
  const getSections = (): string[] => {
    if (!projectFormConfig) return []
    
    const sections = new Set<string>()
    projectFormConfig.projectFields.forEach(field => {
      if (field.section) {
        sections.add(field.section)
      }
    })
    
    return Array.from(sections).sort()
  }

  return {
    projectFormConfig,
    loading: loading || templateLoading,
    error: error || templateError,
    currentTemplate,
    refreshConfig,
    // Helper functions
    getFieldConfig,
    hasField,
    getFieldOptions,
    getFieldLabel,
    getFieldPlaceholder,
    isFieldRequired,
    getFieldsBySection,
    getSections
  }
}